import React, { useMemo, useState } from 'react';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { SearchInput, SpinnerOverlay } from '@components/common';

import { useQueryFoldersMoveDestinations } from '@hooks/portal/folders/useQueryFoldersMoveDestinations';

import { IDestinationFolderForMove } from '../interfaces';

import SelectFolderItem from '../SelectFolderItem';
import { flattenMoveDestinations } from './utils';

import styles from './SelectFolderForMoveModal.module.scss';

interface ISelectFolderModalProps {
	folderId?: string;
	onSelect: (selectedFolder: IDestinationFolderForMove | null) => void;
}

const ROW_HEIGHT = 40;

const SelectFolderForMoveModal = ({
	folderId,
	onSelect,
}: ISelectFolderModalProps) => {
	const {
		foldersMoveDestinations,
		isLoadingFoldersMoveDestinations,
	} = useQueryFoldersMoveDestinations(folderId);

	const [searchValue, setSearchValue] = useState('');

	const onSearchHandler = (value: string) => {
		setSearchValue(value);
	};

	const handleSelect = (selectedFolder: IDestinationFolderForMove) => {
		onSelect(selectedFolder);
	};

	const flatMoveDestinations = useMemo(() => {
		if (!foldersMoveDestinations) {
			return [];
		}

		return flattenMoveDestinations(foldersMoveDestinations);
	}, [foldersMoveDestinations]);

	const filteredMoveDestinations = useMemo(() => {
		const searchQuery = searchValue.trim().toLowerCase();

		if (searchQuery === '') {
			return flatMoveDestinations;
		}

		return flatMoveDestinations.filter(destination => {
			return destination.folder.folderName.toLowerCase().includes(searchQuery);
		});
	}, [flatMoveDestinations, searchValue]);

	const onDestinationSelect = ({ folderId, folderName, folderHasContent }: IDestinationFolderForMove) => {
		handleSelect({
			folderId,
			folderName,
			folderHasContent,
		});
	};

	return (
		<div className={styles.selectFolderContainer}>
			{isLoadingFoldersMoveDestinations && <SpinnerOverlay />}
			<div className={styles.searchInput}>
				<SearchInput
					isClearable
					onSearch={onSearchHandler}
					placeholder='Search by name'
					searchThreshold={0}
					value={searchValue}
				/>
			</div>
			<div className={styles.folderListContainer}>
				<AutoSizer>
					{({ height, width }: Size) => (
						<List
							height={height}
							itemCount={filteredMoveDestinations.length}
							itemSize={ROW_HEIGHT}
							width={width}
						>
							{({ style, index }: ListChildComponentProps) => {
								const { folder, depth } = filteredMoveDestinations[index];

								return (
									<div style={style}>
										<SelectFolderItem
											depth={depth}
											folder={folder}
											onSelect={onDestinationSelect}
										/>
									</div>
								);
							}}
						</List>
					)}
				</AutoSizer>
			</div>
		</div>
	);
};

export default SelectFolderForMoveModal;
